import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { Case } from '@/modules/Case/interfaces/case'

import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors, showNotification } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppRadio from '@/components/AppRadio.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppArcSelector from '@/components/AppArcSelector.vue'
import { getCaseReport, exportCaseReports } from '@/modules/Case/gql/case.gql'

import { policyStatus, policyStatusClass, billingType, statusValues, policyTypeKeys, policyTypeValues } from '@/modules/Policy/helpers/policy'
import ListPage from '@/mixins/list-page'
import SearchCarrier from '@/mixins/search-queries/search-carrier'
import SearchCarrierProduct from '@/mixins/search-queries/search-carrier-product'
import SearchProducer from '@/mixins/search-queries/search-producer'
import SearchGeneralAgent from '@/mixins/search-queries/search-ga'
import SearchBook from '@/mixins/search-queries/search-book'
import SearchUser from '@/mixins/search-queries/search-user'
import RecordTypesHandler from '@/mixins/record-types'
import dollarFilter from '@/utils/dollarFilter'
import ExportExcel from '@/mixins/export-excel'
import { dateToDisplay } from '@/utils/dateTime'
import { SnackType } from '@/interfaces/snack'
import {
  yesOrNoKeys,
  yesOrNoValues
} from '@/modules/Carrier/helpers/carrier'
import { translateCaseTypeKey } from '@/utils/translate'
import { caseType, caseTypeClass, casePriority, casePriorityClass, casePriorityValues, caseTypeValues, casePriorityKeys, caseTypeKeys } from '@/modules/Case/helpers/case'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import { PageState } from '../../../store/modules/page'

export interface InfoItem {
  title: string
  value: string
}

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh,
    AppDialog,
    AppRadio,
    AppTextSearch,
    AppCheckbox,
    AppTableDate,
    AppSelector,
    AppListSearchBtn,
    AppDatePicker,
    AppArcSelector,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleSelector,
    AppSimpleTextSearch,
    AppSimpleDatePicker
  }
})
export default class CaseReports extends mixins(ListPage, mixins(SearchProducer, ExportExcel, SearchUser)) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Reports'
    },
    {
      text: 'Case Reports'
    }
  ]
  cases: Policy[] = []
  caseReports: Policy[] = []
  tableLoading = false

  statisticsLoading = true
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    createdAt: 'CREATED_AT',
    name: 'CASE_NAME',
    closeDate: 'CLOSE_DATE',
    type: 'CASE_TYPE'
  }

  tableColumn = [
    { field: 'id',
      title: 'Case ID',
      sortable: true,
      fixed: 'left',
      width: 110
    },
    { field: 'name',
      title: 'Case Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true,
      width: 200
    },
    {
      field: 'type',
      title: 'Case Type',
      slots: { default: 'type_default' },
      sortable: true,
      resizable: true,
      width: 150
    },
    { title: 'recordType',
      field: 'Record Type',
      slots: { default: 'record_type_default' },
      resizable: true,
      width: 180
    },
    { title: 'Account Name',
      field: 'account',
      slots: { default: 'account_name_default' },
      resizable: true
    },
    { title: 'source',
      field: 'Source of Account',
      slots: { default: 'source_name_default' },
      resizable: true
    },
    {
      title: 'Case Manager',
      field: 'caseManager',
      slots: {
        default: 'case_manager_default'
      },
      resizable: true,
      sortable: false
    },
    {
      title: 'Bind Policy',
      field: 'policies',
      slots: {
        default: 'policy_default'
      },
      resizable: true,
      sortable: false
    },
    { title: 'Create Date',
      field: 'createdAt',
      slots: {
        default: 'create_default'
      },
      sortable: true,
      resizable: true,
      width: 150
    },
    { title: 'Open Days',
      field: 'openDays',
      slots: {
        default: 'openDays_default'
      },
      resizable: true,
      width: 120
    },
    { title: 'Close Date',
      field: 'closeDate',
      slots: {
        default: 'close_default'
      },
      sortable: true,
      resizable: false,
      width: 150,
      fixed: 'right'
    }
  ]

  dateToDisplay = dateToDisplay

  isMounted = false

  generateReportDialog = {
    show: false,
    title: 'Generate Report',
    text: ''
  }

  disableConfirm = false

  yesOrNoVals = yesOrNoKeys
  yesOrNoOptions = yesOrNoValues

  translateCaseTypeKey = translateCaseTypeKey
  caseTypeClass = caseTypeClass

  get reportInfoList () {
    if (!this.filter) {
      return []
    } else {
      const filterInfoList: InfoItem[] = []
      if (this.filter.source && this.filter.source.name) {
        const infoItem: InfoItem = {
          title: 'Source of Account',
          value: this.filter.source.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.caseManager && this.filter.caseManager.username) {
        const infoItem: InfoItem = {
          title: 'Case Manager',
          value: this.filter.caseManager.username
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.ifBindPolicy !== null && !isNaN(this.filter.ifBindPolicy) && this.filter.ifBindPolicy !== -1) {
        const infoItem: InfoItem = {
          title: 'Bind Policy',
          value: this.yesOrNoOptions[this.filter.ifBindPolicy]
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.inquiryName) {
        const infoItem: InfoItem = {
          title: 'Case Name',
          value: this.filter.inquiryName
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.accountName) {
        const infoItem: InfoItem = {
          title: 'Account Name',
          value: this.filter.accountName
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.createStartDate || this.filter.createEndDate) {
        if (this.filter.createStartDate && this.filter.createEndDate) {
          const infoItem: InfoItem = {
            title: 'Case Create Date',
            value: `${dateToDisplay(+this.filter.createStartDate)} - ${dateToDisplay(+this.filter.createEndDate)}`
          }
          filterInfoList.push(infoItem)
        } else {
          const infoItem: InfoItem = {
            title: 'Case Create Date',
            value: `${this.filter.createStartDate ? dateToDisplay(+this.filter.createStartDate) : ''}${this.filter.createEndDate ? dateToDisplay(+this.filter.createEndDate) : ''}`
          }
          filterInfoList.push(infoItem)
        }
      }
      if (this.filter.closeStartDate || this.filter.closeEndDate) {
        if (this.filter.closeStartDate && this.filter.closeEndDate) {
          const infoItem: InfoItem = {
            title: 'Case Close Date',
            value: `${dateToDisplay(+this.filter.closeStartDate)} - ${dateToDisplay(+this.filter.closeEndDate)}`
          }
          filterInfoList.push(infoItem)
        } else {
          const infoItem: InfoItem = {
            title: 'Case Close Date',
            value: `${this.filter.closeStartDate ? dateToDisplay(+this.filter.closeStartDate) : ''}${this.filter.closeEndDate ? dateToDisplay(+this.filter.closeEndDate) : ''}`
          }
          filterInfoList.push(infoItem)
        }
      }
      if (this.filter.isClosed !== null && !isNaN(this.filter.isClosed) && this.filter.isClosed !== -1) {
        const infoItem: InfoItem = {
          title: 'Case Closed',
          value: this.yesOrNoOptions[this.filter.isClosed]
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.openDaysMoreThan) {
        const infoItem: InfoItem = {
          title: 'Open Days >',
          value: this.filter.openDaysMoreThan
        }
        filterInfoList.push(infoItem)
      }
      return filterInfoList
    }
  }

  @State(state => state.page) page!: PageState

  async created () {
    await this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    this.onResize()
    await this.restorePage(this.page[this.$route.name || ''])
    this.checkGetCases()
  }

  filterHandler (type?: string) {
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.source && this.filter.source.id !== undefined) {
      filter.sourceId = Number(this.filter.source.id)
      if (type === 'input') {
        filter.sourceName = this.filter.source.name
      }
    }
    if (this.filter.caseManager && this.filter.caseManager.id !== undefined) {
      filter.caseManagerId = Number(this.filter.caseManager.id)
      if (type === 'input') {
        filter.caseManagerName = this.filter.caseManager.username
      }
    }
    if (this.filter.createStartDate) {
      filter.createStartDate = this.filter.createStartDate.toString()
    }
    if (this.filter.createEndDate) {
      filter.createEndDate = this.filter.createEndDate.toString()
    }
    if (this.filter.closeStartDate) {
      filter.closeStartDate = this.filter.closeStartDate.toString()
    }
    if (this.filter.closeEndDate) {
      filter.closeEndDate = this.filter.closeEndDate.toString()
    }
    if (this.filter.inquiryName) {
      filter.inquiryName = this.filter.inquiryName
    }
    if (this.filter.accountName) {
      filter.accountName = this.filter.accountName
    }
    if (this.filter.openDaysMoreThan) {
      filter.openDaysMoreThan = +this.filter.openDaysMoreThan
    }
    if (this.filter.ifBindPolicy !== null && !isNaN(this.filter.ifBindPolicy) && this.filter.ifBindPolicy !== -1) {
      filter.ifBindPolicy = this.filter.ifBindPolicy === 0
    }
    if (this.filter.isClosed !== null && !isNaN(this.filter.isClosed) && this.filter.isClosed !== -1) {
      filter.isClosed = this.filter.isClosed === 0
    }
    return filter
  }

  checkGetCases () {
    if (!this.filter.createStartDate && !this.filter.createEndDate && !this.filter.closeStartDate && !this.filter.closeEndDate && (this.filter.ifBindPolicy === null || isNaN(this.filter.ifBindPolicy) || this.filter.ifBindPolicy === -1) && (this.filter.isClosed === null || isNaN(this.filter.isClosed) || this.filter.isClosed === -1) && !this.filter.inquiryName && !this.filter.accountName && !this.filter.openDaysMoreThan && (!this.filter.source || !this.filter.source.id) && (!this.filter.caseManager || !this.filter.caseManager.id)) {
      this.cases = []
      this.options.tablePage.total = 0
      return
    }
    this.options.tablePage.enabled = true
    this.getCases()
  }

  onResize () {
    const container = this.$refs.reportscontainer as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  async getCases (pageNumber?: number, perPageSize?: number) {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'ID'
    const filter = this.filterHandler()
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseReport,
        variables: {
          query: filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: pageNumber && perPageSize ? ((pageNumber - 1) * perPageSize) : ((currentPage - 1) * pageSize),
            limit: perPageSize || pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.$R.forEach((caseItem: Case) => {
        const { openDate, closed } = caseItem
        const openDateM = this.$moment.unix(openDate).tz('America/New_York')
        const nowM = this.$moment().tz('America/New_York')

        caseItem.openDays = closed ? '-' : nowM.diff(openDateM, 'days')
      }, result.data.getCaseReport.cases)

      if (pageNumber && perPageSize) {
        this.caseReports = this.caseReports.concat(result.data.getCaseReport.cases)
      } else {
        this.cases = result.data.getCaseReport.cases
      }
      this.isMounted = true
      this.tableLoading = false
      this.options.tablePage.total = result.data.getCaseReport.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.checkGetCases()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getCases()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getCases()
  }

  routeToDetail (item: any) {
    return {
      name: 'CaseDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToPolicyDetail (item: any) {
    return {
      name: 'PolicyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    return {
      name: 'CarrierDetailDetail',
      params: {
        id: item.carrier.id
      }
    }
  }

  routeToCarrierProductDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.carrierProduct.id
      }
    }
  }

  generateHandler () {
    this.generateReportDialog.text = ''
    if (this.reportInfoList.length > 0) {
      for (var i = 0; i < this.reportInfoList.length; i++) {
        this.generateReportDialog.text += `${this.reportInfoList[i].title}:${this.reportInfoList[i].value}; `
      }
    } else {
      this.generateReportDialog.text = 'case report'
    }
    this.generateReportDialog.show = true
  }

  async generateConfirm () {
    const input = this.filterHandler('input')
    try {
      const res = await this.$apollo.mutate({
        mutation: exportCaseReports,
        variables: {
          input
        }
      })
      if (res.data.exportCaseReports) {
        showNotification({
          msg: 'Generate report successfully',
          type: SnackType.Success
        })
      }
      this.generateReportDialog.show = false
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
}
